import { IconButton } from "@/subframe/components/IconButton";
import { Button } from "@/subframe/components/Button";
import { DropdownMenu } from "@/subframe/components/DropdownMenu";
import * as SubframeCore from "@subframe/core";
import { Switch } from "@/subframe/components/Switch";
import { TextArea } from "@/subframe/components/TextArea";
import { TextFieldUnstyled } from "@/subframe/components/TextFieldUnstyled";
import { Recipient } from "../redux/interfaces/reducersInterfaces";
import ReactCountryFlag from "react-country-flag";
import {getCountryCodeFromNumber } from "../Utils/countryNameToCode";
import { MessagesDataWithPagination } from "../Interfaces/messagesInterface";
import { getLatestReadMessage } from "../Utils/messagesHelper";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Alert } from "@/subframe/components/Alert";
import { useDispatch, useSelector } from "react-redux";
import { updateRecipient } from "../redux/reducers/messengerSlice";
import { AppDispatch, RootState } from "../redux/store";
import { formatPhoneNumber } from "../Utils/formatPhoneNumber";
import axios from "axios";

import { addLabel, removeLabel } from "../redux/reducers/authSlice";
import { Loader } from "@/subframe/components/Loader";
import { addNotification } from "../redux/reducers/notificationSlice";
import { NotificationIcon, NotificationType } from "../Utils/notificationEnums";

interface FriendInfoProps {
  currentRecipient: Recipient | null;
  messages: MessagesDataWithPagination;
  isSwitchOn: boolean;
  onToggle: () => void;
}

function FriendInfo({
  currentRecipient,
  messages,
  isSwitchOn,
  onToggle,
}: FriendInfoProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [lastReadStatus, setLastReadStatus] = useState<string | null>(null);
  const [name, setName] = useState(
    currentRecipient?.name || "No name available"
  );
  const nameRef = useRef<HTMLInputElement | null>(null);

  const [globalLabels, setGlobalLabels] = useState<string[]>(
    currentRecipient?.global_label || []
  );
  const { labels, token } = useSelector((state: RootState) => state.auth);
  const [label, setLabel] = useState("");
  const [loadingForLabelCreation, setLoadingForLabelCreation] = useState(false);
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [isNoteEditing, setIsNoteEditing] = useState(false);
  const [note, setNote] = useState(currentRecipient?.note || "");
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const countryCode =
    currentRecipient && getCountryCodeFromNumber(currentRecipient?.phone_number.toString());

  // Get the time difference with Moment.js
  const getTimeDifference = (readTime: number | null) => {
    const currentTime = moment();
    const readMoment = moment(readTime);
    return readMoment.from(currentTime);
  };

  const latestReadMessage =
    messages && messages.items && getLatestReadMessage(messages.items);

  useEffect(() => {
    if (latestReadMessage) {
      const timeDifference = getTimeDifference(latestReadMessage.read_time);
      setLastReadStatus(timeDifference);
    } else {
      setLastReadStatus("No messages have been read.");
    }
  }, [latestReadMessage]);

  //Update Name
  const handleSaveName = () => {
    setIsNameEditing(false);
    if (
      currentRecipient &&
      currentRecipient.id &&
      name !== currentRecipient.name
    ) {
      const { id, manual_label, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          manual_label: Array.isArray(manual_label) ? manual_label : [],
          name,
        })
      );
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.SUCCESS,
          title: "Name updated successfully",
          message: "",
          icon: NotificationIcon.CHECK,
        })
      );
      nameRef.current?.blur();
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSaveName();
    }
  };

  //Update Note
  const handleNoteKeyDown = async (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      await saveNote();
      textAreaRef.current?.blur();
    }
  };

  const handleNoteBlur = async () => {
    if (isNoteEditing) {
      await saveNote();
    }
  };

  const saveNote = async () => {
    setIsNoteEditing(false);

    if (
      currentRecipient &&
      currentRecipient.id &&
      note !== currentRecipient?.note
    ) {
      const { id, manual_label, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          manual_label: Array.isArray(manual_label) ? manual_label : [],
          note,
        })
      );
    }
  };

  useEffect(() => {
    setNote(currentRecipient?.note || "");
  }, [currentRecipient?.note]);

  useEffect(() => {
    setName(currentRecipient?.name || "");
  }, [currentRecipient?.name]);

  const handleRemoveGlobalLabel = (labelToRemove: string) => {
    const updatedLabels = globalLabels?.filter(
      (label) => label !== labelToRemove
    );

    setGlobalLabels(updatedLabels);

    if (currentRecipient && currentRecipient.id) {
      const { id, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          global_label: updatedLabels,
        })
      );
    }
  };

  useEffect(() => {
    setGlobalLabels(currentRecipient?.global_label || []);
  }, [currentRecipient?.global_label]);

  // const handleRemoveManualLabel = (labelToRemove: string) => {
  //   const updatedLabels = manualLabels?.filter(
  //     (label) => label !== labelToRemove
  //   );

  //   setManualLabels(updatedLabels);

  //   if (currentRecipient && currentRecipient.id) {
  //     const { id, ...rest } = currentRecipient;
  //     dispatch(
  //       updateRecipient({
  //         id,
  //         ...rest,
  //         manual_label: updatedLabels,
  //       })
  //     );
  //   }
  // };

  const handleAssignLabel = (label: string) => {
    if (currentRecipient && currentRecipient.id) {
      if (globalLabels.includes(label)) {
        return;
      }

      const updatedLabels = [...(globalLabels || []), label];

      const { id, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          global_label: updatedLabels,
        })
      );
      setGlobalLabels(updatedLabels);
    }
  };

  const handleCreateLabel = async () => {
    if (!label.trim()) {
      return;
    }
    setLoadingForLabelCreation(true);

    try {
      const response = await axios.post(
        "https://api.insightssystem.com/api:uYdlVwac/web/label_management",
        {
          label,
          keywords: [],
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );

      dispatch(addLabel(response.data));
      setLoadingForLabelCreation(true);
      setLabel("");
    } catch (err) {
      console.log(err);
      
    } finally {
      setLoadingForLabelCreation(false);
    }
  };

  const handleDeleteLabel = async (labelId: number) => {
    if (!labelId) return;

    try {
      await axios.delete(
        `https://api.insightssystem.com/api:uYdlVwac/web/label_management/${labelId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );
      dispatch(removeLabel(labelId));
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      {/* Right part */}
      <div className="flex w-80 flex-none flex-col items-start self-stretch border-l border-solid border-neutral-border">
        <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
          <div className="flex w-full flex-col items-start gap-6">
            <div className="flex items-start gap-3">
              <div className="flex flex-col flex-wrap items-center justify-center gap-2 relative">
                <svg
                  className="h-12 w-12 flex-none rounded-full"
                  xmlns="http://www.w3.org/2000/svg"
                  width="401px"
                  height="401px"
                  enable-background="new 312.809 0 401 401"
                  version="1.1"
                  viewBox="312.809 0 401 401"
                >
                  <g transform="matrix(1.223 0 0 1.223 -467.5 -843.44)">
                    <rect
                      x="601.45"
                      y="653.07"
                      width="401"
                      height="401"
                      fill="#E4E6E7"
                    />
                    <path
                      d="m802.38 908.08c-84.515 0-153.52 48.185-157.38 108.62h314.79c-3.87-60.44-72.9-108.62-157.41-108.62z"
                      fill="#AEB4B7"
                    />
                    <path
                      d="m881.37 818.86c0 46.746-35.106 84.641-78.41 84.641s-78.41-37.895-78.41-84.641 35.106-84.641 78.41-84.641c43.31 0 78.41 37.9 78.41 84.64z"
                      fill="#AEB4B7"
                    />
                  </g>
                </svg>
                <div className="flex h-7 w-7 flex-none items-center rounded-full pl-3 pt-2 absolute -bottom-2">
                  <div className="flex items-center gap-3.5">
                    <div>
                      <div className=" flex items-center rounded-full border-[3px] border-solid border-red h-5 w-5 flex-none bg-white">
                        <ReactCountryFlag
                          style={{
                            width: "5em",
                            height: "5em",
                          }}
                          countryCode={countryCode?.countryCode ? countryCode.countryCode : "US"}
                          svg
                        />
                      </div>
                    </div>
                    <span className="min-w-[176px] grow shrink-0 basis-0 text-caption font-caption text-subtext-color">
                      {currentRecipient && currentRecipient.country
                        ? currentRecipient.country
                        : "No country name available"}
                    </span>
                  </div>
                </div>
              </div>
              <div
                className="flex flex-col items-start"
                title={!isNameEditing ? "Tap to edit name" : ""}
              >
                <TextFieldUnstyled>
                  <TextFieldUnstyled.Input
                    ref={nameRef}
                    placeholder="No name available"
                    value={name}
                    onFocus={() => setIsNameEditing(true)}
                    onBlur={handleSaveName}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={handleNameKeyDown}
                    style={{
                      cursor: isNameEditing ? "text" : "pointer",
                      outline: "none",
                      border: isNameEditing ? "1px solid #007bff" : "none",
                      borderRadius: "4px",
                      backgroundColor: isNameEditing
                        ? "#f8f9fa"
                        : "transparent",
                      width: "100%",
                    }}
                    onClick={() => {
                      if (!isNameEditing) {
                        setIsNameEditing(true);
                      }
                    }}
                  />
                </TextFieldUnstyled>
                <span className="text-caption-bold font-caption-bold text-default-font">
                  {countryCode?.countryCode && currentRecipient?.phone_number
                    ? formatPhoneNumber(
                        currentRecipient.phone_number,
                        countryCode.countryCode
                      )
                    : currentRecipient?.phone_number
                    ? currentRecipient.phone_number
                    : "No number available"}
                    {/* {currentRecipient?.phone_number} */}
                </span>
              </div>
            </div>
            <div className="flex w-full flex-col items-start gap-1">
              <span className="text-caption-bold font-caption-bold text-subtext-color">
                Last Read
              </span>
              <span className="text-body font-body text-default-font">
                {lastReadStatus}
              </span>
            </div>
            <div className="flex w-full flex-col items-start gap-1">
              <div className="flex items-center">
                <span className="text-caption-bold font-caption-bold text-subtext-color">
                  Label
                </span>
                <IconButton
                  size="small"
                  icon="FeatherSettings"
                  // onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                />
              </div>
              <div className="flex w-full flex-wrap items-center gap-2">
                <SubframeCore.Icon
                  className="text-caption font-caption text-neutral-700"
                  name={null}
                />
                {globalLabels?.map((label, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1 rounded-md border border-neutral-border bg-neutral-100 pl-2 pr-1 py-0.5"
                  >
                    <span className="whitespace-nowrap text-caption font-caption text-neutral-700">
                      {label}
                    </span>
                    <IconButton
                      size="small"
                      icon="FeatherX"
                      onClick={() => handleRemoveGlobalLabel(label)}
                    />
                  </div>
                ))}
                <SubframeCore.Icon
                  className="text-caption font-caption text-neutral-700"
                  name={null}
                />
                <SubframeCore.DropdownMenu.Root>
                  <SubframeCore.DropdownMenu.Trigger asChild={true}>
                    <Button
                      className="h-8 grow shrink-0 basis-0"
                      variant="neutral-secondary"
                      iconRight="FeatherChevronDown"
                      // onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                    >
                      Create/Assign
                    </Button>
                  </SubframeCore.DropdownMenu.Trigger>
                  <SubframeCore.DropdownMenu.Portal>
                    <SubframeCore.DropdownMenu.Content
                      side="bottom"
                      align="start"
                      sideOffset={2}
                      asChild={true}
                    >
                      <DropdownMenu>
                        <div className="flex items-center">
                          <TextFieldUnstyled>
                            <TextFieldUnstyled.Input
                              className="h-8 w-full flex-none"
                              placeholder="Create Label "
                              value={label}
                              onChange={(e) => setLabel(e.target.value)}
                              disabled={loadingForLabelCreation}
                            />
                          </TextFieldUnstyled>
                          {loadingForLabelCreation ? (
                            <Loader size="small" />
                          ) : (
                            <IconButton
                              variant="inverse"
                              size="small"
                              icon="FeatherCheck"
                              onClick={handleCreateLabel}
                              disabled={loadingForLabelCreation}
                            />
                          )}
                        </div>
                        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                        {labels?.map((labelItem) => {
                          // Check if the label is already in global_label array
                          const isAssigned = globalLabels.includes(
                            labelItem.label
                          );

                          return (
                            <div
                              key={labelItem.id}
                              className="flex w-full items-center gap-2"
                            >
                              <DropdownMenu.DropdownItem
                                icon={null}
                                className={`${
                                  isAssigned
                                    ? "pointer-events-none bg-gray-100"
                                    : ""
                                }`}
                                onClick={() =>
                                  handleAssignLabel(labelItem.label)
                                }
                              >
                                <div className="flex items-center justify-between w-full">
                                  <span>{labelItem.label}</span>
                                  {isAssigned && (
                                    <span className="text-gray-500 text-sm ml-2">
                                      Already assigned
                                    </span>
                                  )}
                                </div>
                              </DropdownMenu.DropdownItem>
                              <IconButton
                                size="small"
                                icon="FeatherX"
                                className="text-red-500"
                                onClick={() => handleDeleteLabel(labelItem.id)}
                              />
                            </div>
                          );
                        })}
                      </DropdownMenu>
                    </SubframeCore.DropdownMenu.Content>
                  </SubframeCore.DropdownMenu.Portal>
                </SubframeCore.DropdownMenu.Root>
              </div>
            </div>
            <div className="flex w-full flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-2.5 py-2 shadow-sm">
              <span className="text-caption-bold font-caption-bold text-subtext-color">
                Chat Handoff
              </span>
              <div className="flex w-full items-center justify-center gap-2">
                <SubframeCore.Icon
                  className="text-caption font-caption text-neutral-700"
                  name={null}
                />
                <SubframeCore.Icon
                  className="text-caption font-caption text-neutral-700"
                  name={null}
                />
                <span className="grow shrink-0 basis-0 text-body font-body text-default-font text-center">
                  Human Agent
                </span>
                <Switch checked={isSwitchOn} onCheckedChange={onToggle} />
                <span className="grow shrink-0 basis-0 text-body font-body text-default-font text-center">
                  AI Assistant
                </span>
              </div>
            </div>
            <Alert
              title="AI Assistant"
              description={
                "Enabled: Messages will be shared with your connected automation tools (Zapier, Make, etc.)\nDisabled: Messages won't sync with automation tools"
              }
            />
          </div>
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Notes
            </span>
            <TextArea
              className="h-auto w-full flex-none"
              variant="filled"
              label=""
              helpText=""
            >
              <TextArea.Input
                ref={textAreaRef}
                className="min-h-[96px]"
                placeholder="Add notes about this contact..."
                value={note}
                onFocus={() => setIsNoteEditing(true)}
                onChange={(e) => setNote(e.target.value)}
                onBlur={handleNoteBlur}
                onKeyDown={handleNoteKeyDown}
              />
            </TextArea>
          </div>
          {currentRecipient?.note_auto && (
            <div className="flex w-full flex-col items-start gap-2">
              {/* <div className="flex items-center gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Automation Note
                </span>
                <IconButton
                  size="small"
                  icon="FeatherInfo"
                  title="This note was automatically generated by your connected automation tools (Make.com, Zapier, or n8n) and cannot be manually edited."
                />
              </div> */}
              <div className="flex w-full flex-col items-start gap-1">
                <div className="w-full rounded-md border border-neutral-border bg-neutral-50 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-blue-600">Automation Note</span>
                  </div>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {currentRecipient.note_auto}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default FriendInfo;
